// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { all, call, delay, put, select, takeEvery } from 'redux-saga/effects';
import { AgGridUtils } from '@cosmotech/core';
import { Api } from '../../../services/config/Api';
import { TWINGRAPH_QUERIES_DELAY } from '../../../services/config/FunctionalConstants';
import WorkspaceService from '../../../services/workspace/WorkspaceService';
import { DatasetsUtils } from '../../../utils';
import { WizardUtils } from '../../../utils/WizardUtils';
import { WIZARD_TWINGRAPH_QUERIES } from '../WizardConstants';

const getOrganizationId = (state) => state.organization.current?.data?.id;
const getWorkspaceId = (state) => state.workspace.current?.data?.id;
const getDatasets = (state) => state.dataset.list?.data;
const getSolutionData = (state) => state.solution.current?.data;

function* getOneQuery(datasetId, scenarioId, query) {
  const organizationId = yield select(getOrganizationId);
  try {
    const { data } = yield call(Api.Datasets.twingraphQuery, organizationId, datasetId, { query: query.queryString });
    const activityDetails = data.map((r) => r.n.properties);
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.PROCESS_TECHNICAL_POLICY_TWINGRAPH_QUERIES,
      payload: { result: activityDetails, scenarioId, key: query.key, gisementName: query.gisementName },
    });
  } catch (e) {
    console.error(e);
  }
}

function* downloadFileData(scenarioId, file) {
  const organizationId = yield select(getOrganizationId);
  const workspaceId = yield select(getWorkspaceId);
  const datasets = yield select(getDatasets);
  const dataset = datasets?.find((dataset) => dataset.id === file.value);
  const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
  const data = yield call(WorkspaceService.downloadWorkspaceFileData, organizationId, workspaceId, storageFilePath);
  const fileContentFirstLine = data.split('\n')[0].split(',');
  const columns = fileContentFirstLine.map((col) => ({ field: col, acceptsEmptyFields: true }));
  const parsedData = AgGridUtils.fromCSV(data, true, columns);
  yield put({
    type: WIZARD_TWINGRAPH_QUERIES.PROCESS_TECHNICAL_POLICY_TWINGRAPH_QUERIES,
    payload: { scenarioId, result: parsedData.rows, key: file.key, gisementName: file.gisementName },
  });
}

function* getSelectedGisementTwingraphQueries(action) {
  const solutionData = yield select(getSolutionData);
  const technicalPolicyParameter = solutionData.parameters.find(
    (parameter) => parameter.options?.subType === 'TECHNICALPOLICY'
  );
  try {
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'LOADING_GISEMENT_DATA',
    });
    const { selectedGisementTablesMapping, datasetId, scenarioData } = action.payload;
    const technicalPolicyParameterValue = scenarioData.parametersValues.find(
      (parameter) => parameter.parameterId === technicalPolicyParameter.id
    )?.value;
    const parsedScenarioParameters = technicalPolicyParameterValue ? JSON.parse(technicalPolicyParameterValue) : [];
    const parsedScenarioParametersIds = parsedScenarioParameters.map((parameter) => parameter.parameterId);
    const files = [];
    const queries = [];
    for (const [key, value] of Object.entries(selectedGisementTablesMapping)) {
      if (!['id', 'ReglesGestion', 'Gisement', 'ClasseActif', 'SequencesActionsImposees'].includes(key)) {
        if (parsedScenarioParametersIds.includes(value)) {
          const parameterValue = parsedScenarioParameters.find((parameter) => parameter.parameterId === value).value;
          files.push({
            value: parameterValue,
            gisementName: selectedGisementTablesMapping.Gisement,
            key,
          });
        } else {
          queries.push({
            queryString: WizardUtils.buildQueryString(value),
            key,
            gisementName: selectedGisementTablesMapping.Gisement,
          });
        }
      }
    }

    yield all(
      queries.map((query, index) => {
        if (index !== 0) delay(TWINGRAPH_QUERIES_DELAY);
        return call(getOneQuery, datasetId, scenarioData.id, query);
      })
    );
    yield all(
      files.map((file, index) => {
        if (index !== 0) delay(TWINGRAPH_QUERIES_DELAY);
        return call(downloadFileData, scenarioData.id, file);
      })
    );
    yield put({
      type: WIZARD_TWINGRAPH_QUERIES.SET_TWINGRAPH_QUERIES_STATUS,
      status: 'IDLE',
    });
  } catch (e) {
    console.error(e);
  }
}

function* selectedGisementTwingraphQueriesSaga() {
  yield takeEvery(
    [WIZARD_TWINGRAPH_QUERIES.INITIALIZE_SELECTED_GISEMENT_TWINGRAPH_QUERIES],
    getSelectedGisementTwingraphQueries
  );
}
export default selectedGisementTwingraphQueriesSaga;
